// =============================================================================
// components/learn/StepCard.tsx — gradient step card for Learn with CRYB.
//
// The reference design (old app, client-approved): vivid gradient cover cards
// with a "Step 01" kicker and a big white title. Each card is one article; the
// step number is its position within the selected level, so a level reads as a
// progressive course. Gradient varies per card (components/learn/gradients).
// If the article ships its own cover image we show it and lay the same text
// treatment over a dark scrim so titles stay readable.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import CachedImage from '../ui/CachedImage';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from '../ui/Icon';
import { spacing, radius, fonts } from '../../constants/theme';
import { gradientFor } from './gradients';
import { learnCover } from '../../lib/icons';

export interface StepCardArticle {
  slug?: string;
  title?: string;
  description?: string;
  excerpt?: string;
  image?: string;
  imageUrl?: string;
}

interface Props {
  article: StepCardArticle;
  /** 0-based position within the selected level → "Step 01", "Step 02", … */
  index: number;
  onPress: () => void;
  style?: object;
  /** Locked = earlier lesson not yet passed. Dims + shows a lock, blocks open. */
  locked?: boolean;
  /** Passed = quiz cleared → show a completed check. */
  passed?: boolean;
}

export default function StepCard({ article, index, onPress, style, locked, passed }: Props) {
  const step = String(index + 1).padStart(2, '0');
  // #44: always show a real featured image — the article's own, else a stable
  // topic-themed photo (never the flat gradient placeholder).
  const cover = learnCover(article.title, article.slug, article.image || article.imageUrl, 800);
  const gradient = gradientFor(index);

  const content = (
    <>
      <View style={styles.topRow}>
        <View style={styles.stepPill}>
          <Text style={styles.stepText}>Step {step}</Text>
        </View>
        {passed ? (
          <View style={styles.statusBadge}>
            <Ionicons name="checkmark" size={13} color="#FFFFFF" />
          </View>
        ) : locked ? (
          <View style={styles.statusBadge}>
            <Ionicons name="lock-closed" size={12} color="#FFFFFF" />
          </View>
        ) : null}
      </View>
      <View style={styles.bottomBlock}>
        <Text style={styles.title} numberOfLines={2}>
          {article.title || 'Untitled'}
        </Text>
        <View style={styles.footerRow}>
          <Text style={styles.footerText} numberOfLines={1}>
            {article.description || article.excerpt || 'Read the lesson'}
          </Text>
          <View style={styles.arrowCircle}>
            <Ionicons name="arrow-forward" size={14} color="#FFFFFF" />
          </View>
        </View>
      </View>
    </>
  );

  return (
    <TouchableOpacity
      style={[styles.card, style, locked && styles.cardLocked]}
      activeOpacity={0.85}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ disabled: !!locked }}
      accessibilityLabel={
        locked
          ? `Locked — Step ${step}: ${article.title || 'lesson'}. Pass the previous lesson to unlock.`
          : `Step ${step}: ${article.title || 'lesson'}`
      }
    >
      {cover ? (
        <View style={styles.cover}>
          <CachedImage source={{ uri: cover }} style={StyleSheet.absoluteFill} resizeMode="cover" />
          {/* Scrim keeps the white title readable over any photo. */}
          <LinearGradient
            colors={['rgba(26,26,46,0.15)', 'rgba(26,26,46,0.72)']}
            style={[StyleSheet.absoluteFill, styles.coverInner]}
          >
            {content}
          </LinearGradient>
        </View>
      ) : (
        <LinearGradient
          colors={[...gradient]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[styles.cover, styles.coverInner]}
        >
          {/* Oversized ghost step number gives the gradient cover depth. */}
          <Text style={styles.ghostNumber}>{step}</Text>
          {content}
        </LinearGradient>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: radius.xl,
    overflow: 'hidden',
    shadowColor: 'rgba(108,92,231,0.35)',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 3,
  },
  cover: {
    // FIXED height (was minHeight) → every card in the 2-up grid is identical
    // height, so a row of two aligns perfectly and the odd full-width last card
    // matches. Tall enough that a 2-line title + footer never reach the clipped
    // (overflow:hidden) edge — the "Read the lesson" clip was the footer landing
    // in the padding zone of a too-short minHeight box.
    height: 188,
    borderRadius: radius.xl,
    overflow: 'hidden',
  },
  coverInner: {
    padding: spacing.md,
    paddingBottom: spacing.md + 2,
    justifyContent: 'space-between',
  },
  ghostNumber: {
    position: 'absolute',
    right: -6,
    top: -18,
    fontSize: 96,
    color: 'rgba(255,255,255,0.14)',
    ...fonts.extrabold,
  },
  cardLocked: {
    opacity: 0.55,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.28)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepPill: {
    backgroundColor: 'rgba(255,255,255,0.24)',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 4,
  },
  stepText: {
    fontSize: 11,
    color: '#FFFFFF',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    ...fonts.bold,
  },
  bottomBlock: {
    gap: spacing.sm,
  },
  title: {
    fontSize: 17,
    lineHeight: 22,
    color: '#FFFFFF',
    ...fonts.extrabold,
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  footerText: {
    flex: 1,
    fontSize: 11,
    color: 'rgba(255,255,255,0.82)',
    ...fonts.medium,
  },
  arrowCircle: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.24)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
